import { HttpService, Query, QueryHandler, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { CalendarEventKind, PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { Mutable } from '@skylab-shared/types';
import { Transform } from 'class-transformer';

export interface EventItem<TAuxData = EventDocumentDueDate> {
    readonly id: number;
    readonly date: Date;
    readonly kind: CalendarEventKind;
    readonly auxData: TAuxData | null;
}

export interface EventDocumentDueDate {
    readonly document: string;
    readonly documentId: number;
    readonly documentKind: SaleDocumentKind | PurchaseDocumentKind;
    readonly client: string;
    readonly clientId: number;
    readonly amount: number;
    readonly currency: string;
}

class CalendarEventInternalItemDTO {
    public readonly auxData: {
        readonly amount: number,
        readonly client: string,
        readonly clientId: number,
        readonly currency: string,
        readonly document: string,
        readonly documentId: number,
        readonly documentKind: SaleDocumentKind
    };

    @Transform(transformDTODate, { toClassOnly: true }) public readonly date: Date;
    public readonly id: number;
    public readonly kind: CalendarEventKind;
}

export class CalendarEventsQuery extends Query<EventItem[]> {
    public constructor(public readonly params: {
        readonly minDate?: Date;
        readonly maxDate?: Date;
        readonly pageSize?: number;
    } = { pageSize: 50 }) {
        super();
    }
}

export class CalendarEventsQueryHandlerBase implements QueryHandler<CalendarEventsQuery, EventItem[]> {
    private static Url = TaggedUrl.join('calendar/events', [Tags.Api]);

    public constructor(private readonly $http: HttpService) { }

    public async handleAsync(query: CalendarEventsQuery): Promise<EventItem[]> {
        const { data } = await this.$http.get<CalendarEventInternalItemDTO[]>(CalendarEventsQueryHandlerBase.Url, {
            params: { ...query.params },
            dto: CalendarEventInternalItemDTO
        });

        return data.map(e => {
            const eventItem = <Mutable<EventItem>> {
                id: e.id,
                date: e.date,
                kind: e.kind,
                auxData: null
            };

            if (e.auxData && e.kind === CalendarEventKind.DocumentDueDate) {
                eventItem.auxData = {
                    document: e.auxData.document,
                    documentId: e.auxData.documentId,
                    documentKind: e.auxData.documentKind,
                    client: e.auxData.client,
                    clientId: e.auxData.clientId,
                    amount: e.auxData.amount,
                    currency: e.auxData.currency
                };
            }

            return eventItem;
        });
    }
}
